import Dexie, { Table } from 'dexie';
import type { GridStackWidget } from 'gridstack';
import { isEmpty } from 'ramda';
import DataRun from './data-run.ts';
import { SensorData } from '../../Components/Dashboard/dashboardTypes.ts';

export interface ProgramRun {
  programId: string;
  runs: number[];
}

export interface RunData {
  programId: string;
  runId: number;
  sensorData: SensorData[];
  dashboardLayout: GridStackWidget[];
}

export class SensorDatabase extends Dexie {
  programRuns!: Table<ProgramRun, string>;
  runData!: Table<RunData, (string | number | undefined)[]>;

  constructor() {
    super('sensorDatabase');
    this.version(1).stores({
      programRuns: 'programId',
      runData: '[programId+runId], &runId',
    });
  }
}

export const database = new SensorDatabase();

let activeRun: DataRun | null;

/**
 * Creates a new data run
 *
 * @param {string} programId - program id for the program run
 * @returns {DataRun} A new DataRun object
 */
export const createSensorRun = (programId: string) => {
  activeRun = new DataRun(programId);
  return activeRun;
};

/**
 * Adds data to the current data run (every sample is stored; throttling removed for field testing).
 *
 * @param {SensorData} data - data point to store in memory
 * @returns last sample after append when a run exists, otherwise false
 */
export const setActiveRunDashboardLayout = (layout: GridStackWidget[]) => {
  if (activeRun) {
    activeRun.dashboardLayout = layout;
  }
};

export const addDataToSensorRun = (data: SensorData) => {
  if (activeRun) {
    activeRun.addData(data);
    return activeRun.lastData;
  }
  return false;
};

/**
 * Returns samples from the in-memory active run for re-hydrating live charts.
 * When `maxPoints` is omitted or not a positive finite number, returns the full run.
 */
export const getActiveRunTail = (maxPoints?: number): SensorData[] => {
  if (!activeRun || !Array.isArray(activeRun.data)) return [];
  if (maxPoints != null && Number.isFinite(maxPoints) && maxPoints > 0) {
    return activeRun.data.slice(-maxPoints);
  }
  return activeRun.data.slice();
};

/**
 * Prunes the records down to 5 for any given program
 *
 * @param {object} records - Database records
 * @param {string} programId - Program identifier
 * @returns {object} pruned record
 */
const pruneOldProgramRuns = (records: number[], programId: string) => {
  while (records.length > 5) {
    const runIdToRemove = records.pop();
    database.runData.delete([programId, runIdToRemove]);
  }
  return records;
};

/**
 * Commits data to the database in the appropriate structure
 *
 * @param {DataRun} data - the data to be committed to the database, must have key programId
 * @returns {promise} promise containing the record written to the database
 * @returns {promise} promise containing the error message
 */
const commitData = async (data: DataRun) => {
  const record = await database.programRuns.get(data.programId);
  try {
    if (!record) {
      const dataObject: { programId: string; runs: number[] } = { programId: data.programId, runs: [] };
      dataObject.runs.unshift(data.runId);
      await database.programRuns.add(dataObject);
    } else {
      record.runs.unshift(data.runId);
      record.runs = pruneOldProgramRuns(record.runs, data.programId);
      await database.programRuns.update(data.programId, { runs: record.runs });
    }
    await database.runData.add({
      programId: data.programId,
      runId: data.runId,
      sensorData: data.data,
      dashboardLayout: data.dashboardLayout,
    });
  } catch (err) {
    console.error(`Database failed to save! ${err}`);
  }
};

const clearDataRun = () => {
  activeRun = null;
};

/**
 * Commits data run to database
 */
export const saveSensorRun = async () => {
  if (activeRun && !isEmpty(activeRun.data)) {
    try {
      console.log('Commiting data', activeRun);
      await commitData(activeRun);
    } catch {
      await clearDataRun();
    }
  }
  clearDataRun();
};
